/**
 * Delete Pickup Location Dialog
 * Confirmation dialog for deleting a pickup location
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { PickupLocation } from '@/types/api';

interface DeletePickupLocationDialogProps {
  location: PickupLocation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
}

export function DeletePickupLocationDialog({
  location,
  open,
  onOpenChange,
  onConfirm,
}: DeletePickupLocationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Pickup Location</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this pickup location?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-md border p-4 bg-muted">
            <h4 className="font-semibold mb-1">{location.name}</h4>
            {location.address && (
              <p className="text-sm text-muted-foreground">{location.address}</p>
            )}
          </div>

          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm">
            <p className="font-medium text-yellow-900 mb-1">⚠️ Warning</p>
            <p className="text-yellow-800">
              This action cannot be undone. The location can only be deleted if no active
              members are assigned to it.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
          >
            Delete Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
